const { COUNTRY_PROVINCES } = typeof require !== "undefined" ? require("./utils/countryProvince") : {};

// i18n
const DEFAULT_LANGUAGE = "en";
const DEBUG_LANGUAGE = "dev";
// t("LANGUAGE.en")
// t("LANGUAGE.de")
// t("LANGUAGE.fr")
// t("LANGUAGE.ja")
// t("LANGUAGE.es")
// t("LANGUAGE.pt-BR")
const SUPPORTED_LANGUAGES = [DEFAULT_LANGUAGE, "de", "fr", "ja", "es", "pt-BR"];
const I18N_CLIENT_NS = ["client", "common"];
const I18N_T9N_DOMAIN = "https://weblate.foundryvtt-hub.com";
const I18N_T9N_PROJECT = "the-forge";
const I18N_T9N_COMPLETE_LANGUAGES = [DEFAULT_LANGUAGE]; // Add more languages as translations are fully completed
const I18N_COUNTRY_CODE_MAPPING = {
    en: "us", // English -> US (Because we use US English)
    es: "es", // Spanish -> Spain
    fr: "fr", // French -> France
    pt: "br", // Portuguese -> Brazil
    de: "de", // German -> Germany
    ja: "jp", // Japanese -> Japan
    // Add others as required
};

const KB = 1024;
const MB = 1024 * 1024;
const GB = 1024 * 1024 * 1024;

const SECOND_MS = 1000;
const MINUTE_MS = SECOND_MS * 60;
const HOUR_MS = MINUTE_MS * 60;
const DAY_MS = HOUR_MS * 24;
const YEAR_MS = DAY_MS * 365;
const MONTH_MS = YEAR_MS / 12;

const SHOW_SCHEDULE_SESSION_GRACE_PERIOD = 4; // hours
const CLIENT_TRANSLATIONS_CACHE_MAX_AGE = 5 * MINUTE_MS;

const FEATURED_SORT_DAY_FALLOFF = 45; // days
const FEATURED_SORT_INSTALLS_WEIGHT = 2;
const FEATURED_SORT_INSTALLS_WEIGHT_CEIL = 3.5;

const PACKAGE_MEDIA_TYPES = {
    COVER: "cover",
    ICON: "icon",
    SCREENSHOT: "screenshot",
    VIDEO: "video",
};

const PERMISSIVE_EMAIL_REGEX = /\b(\S+)@(\S+)\.(\S{2,})\b/g;

/**
 * @typedef PricePlan
 * An object reflecting a subscription plan.
 *
 * @property {string} title  The title of the plan
 * @property {string} subtitle
 * @property {string} featureCaption
 * @property {string} registerRoute
 * @property {PricePlanFeature[]} features  The features for this plan.
 * @property {PricePlanPrice} price  The price data for this plan.
 */

/**
 * @typedef PricePlanFeature
 * An object reflecting the features of a given plan.
 *
 * @property {string} text  The text content for this feature
 * @property {boolean} isNew  Is this a new feature?
 */

/**
 * @typedef PricePlanPrice
 * An object reflecting the price data for a given plan.
 *
 * @property {string} annual  How much this plan costs per year.
 * @property {string} annualByMonth  How much this plan costs per year, broken down by month.
 * @property {string} monthly  How much this plan costs per month.
 */

/**
 * Price plan data. Currently used on https://forge-vtt.com/plans.
 *
 * Note: The string values here must be localized.
 *
 * @type {PricePlan[]}
 */
const PAID_PLANS = [
    {
        // t("Game Master")
        title: "Game Master",
        // t("The Foundry experience")
        subtitle: "The Foundry experience",
        price: { annual: "47.99", annualByMonth: "3.99", monthly: "4.99" },
        // t("More than enough for most GMs")
        featureCaption: "More than enough for most GMs",
        features: [
            // t("Generous data storage quotas")
            { text: "Generous data storage quotas" },
            // t("Super fast, always-available games")
            { text: "Super fast, always-available games" },
            // t("Create as many worlds as you want")
            { text: "Create as many worlds as you want" },
            // t("Import and export your games")
            { text: "Import and export your games" },
        ],
        registerRoute: "/signup?returnTo=/account%23membership%26subscribe=game-master",
    },
    {
        // t("Story Teller")
        title: "Story Teller",
        isPopular: true,
        // t("The Forge experience")
        subtitle: "The Forge experience",
        price: { annual: "96.99", annualByMonth: "8.08", monthly: "8.99" },
        // t("All Game Master features, plus")
        featureCaption: "All Game Master features, plus",
        features: [
            // t("Increased data storage quotas")
            { text: "Increased data storage quotas" },
            // t("The Forge's unique Game Manager")
            { text: "The Forge's unique Game Manager" },
            // t("Advanced User Management")
            { text: "Advanced User Management" },
            // t("D&D Beyond™ Integration")
            { text: "D&D Beyond™ Integration" },
        ],
        registerRoute: "/signup?returnTo=/account%23membership%26subscribe=story-teller",
    },
    {
        // t("World Builder")
        title: "World Builder",
        // t("The Ultimate experience")
        subtitle: "The Ultimate experience",
        price: { annual: "139.99", annualByMonth: "11.66", monthly: "12.99" },
        // t("All Game Master and Story Teller features, plus")
        featureCaption: "All Game Master and Story Teller features, plus",
        features: [
            // t("Garganutan data storage quotas")
            { text: "Garganutan data storage quotas" },
            // t("LiveKit servers")
            { text: "LiveKit servers" },
            // t("Save Points")
            { text: "Save Points" },
            // t("Automatic game snapshots")
            { text: "Automatic game snapshots" },
        ],
        registerRoute: "/signup?returnTo=/account%23membership%26subscribe=world-builder",
    },
];

const ASSETS_EXTENSIONS = {
    FOUNDRY_IMAGE: [".jpg", ".jpeg", ".png", ".svg", ".webp"], // Image extensions allowed by Foundry VTT
    IMAGE: [".jpg", ".jpeg", ".png", ".apng", ".svg", ".webp", ".gif", ".avif"], // We allow a few more
    VIDEO: [".mp4", ".ogg", ".webm", ".m4v"],
    AUDIO: [".flac", ".mp3", ".m4a", ".ogg", ".wav", ".webm", ".opus"],
    FONT: [".otf", ".ttf", ".woff", ".woff2"],
    THREE_D: [".glb", ".fbx", ".exr", ".gltf", ".bin"],
    OTHER_MEDIA: [".pdf", ".dd2vtt", ".df2vtt", ".uvtt", ".basis", ".ktx2"],
    COMPRESSED: [".zip", ".rar", ".7z", ".tar", ".gz", ".bz2", ".xz"],
};
ASSETS_EXTENSIONS.MEDIA_EXTENSIONS = [
    ...ASSETS_EXTENSIONS.IMAGE,
    ...ASSETS_EXTENSIONS.VIDEO,
    ...ASSETS_EXTENSIONS.AUDIO,
    ...ASSETS_EXTENSIONS.FONT,
    ...ASSETS_EXTENSIONS.THREE_D,
    ...ASSETS_EXTENSIONS.OTHER_MEDIA,
];

/**
 * Price plan comparison table data. Currently used on https://forge-vtt.com/plans.
 */

if (typeof module !== "undefined") {
    // Running in node
    if (process.env.NODE_ENV === "development") {
        SUPPORTED_LANGUAGES.push(DEBUG_LANGUAGE);
    }
    module.exports = {
        CLIENT_TRANSLATIONS_CACHE_MAX_AGE,
        DEFAULT_LANGUAGE,
        DEBUG_LANGUAGE,
        GAME_SLUG_MIN_LENGTH: 3,
        GAME_SLUG_MAX_LENGTH: 63,
        SUPPORTED_LANGUAGES,
        I18N_CLIENT_NS,
        I18N_T9N_DOMAIN,
        I18N_T9N_PROJECT,
        I18N_T9N_COMPLETE_LANGUAGES,
        I18N_COUNTRY_CODE_MAPPING,
        KB,
        MB,
        GB,
        SECOND_MS,
        MINUTE_MS,
        HOUR_MS,
        DAY_MS,
        MONTH_MS,
        YEAR_MS,
        SHOW_SCHEDULE_SESSION_GRACE_PERIOD,
        PACKAGE_MEDIA_TYPES,
        COUNTRY_PROVINCES,
        PAID_PLANS,
        FEATURED_SORT_DAY_FALLOFF,
        FEATURED_SORT_INSTALLS_WEIGHT,
        FEATURED_SORT_INSTALLS_WEIGHT_CEIL,
        PERMISSIVE_EMAIL_REGEX,
        ASSETS_EXTENSIONS,
    };
} else {
    // Running in browser
    if (window.location.hostname.match(/(?:^|\.)(?:dev|qa|local)\.forge-vtt\.com/)) {
        SUPPORTED_LANGUAGES.push(DEBUG_LANGUAGE);
    }
    window.PACKAGE_MEDIA_TYPES = PACKAGE_MEDIA_TYPES;
}
